import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, Loader2, Undo2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const n = (v) => (v || 0).toLocaleString();
const money = (v) => `$${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function Counts({ counts }) {
  const cells = [
    ['Rows in file', counts.rows, C.navy],
    ['Inserted', counts.inserted, counts.inserted ? C.green : C.slate],
    ['Rejected rows', counts.rejected_rows, counts.rejected_rows ? C.red : C.slate],
    ['Already in platform', counts.conflicts, counts.conflicts ? C.amber : C.slate],
    ['Unresolved references', counts.unresolved_references, counts.unresolved_references ? C.red : C.slate],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="loader-counts">
      {cells.map(([label, value, color]) => (
        <div key={label} className="p-2 rounded-sm" style={{ background: 'rgba(136,150,168,0.07)' }}>
          <p className="text-[9px] uppercase tracking-wider" style={{ color: C.slate }}>{label}</p>
          <p className="text-base font-bold font-mono" style={{ color }}>{n(value)}</p>
        </div>
      ))}
    </div>
  );
}

function Reconciliation({ rec }) {
  if (!rec) return null;
  return (
    <div className="space-y-1.5" data-testid="loader-reconciliation">
      <div className="flex items-center gap-2">
        <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>Reconciliation</p>
        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" data-testid="loader-balanced-badge"
          style={{ background: (rec.balanced ? C.green : C.red) + '18', color: rec.balanced ? C.green : C.red }}>
          {rec.balanced ? 'balanced' : 'does not balance'}
        </span>
        <span className="text-[10px]" style={{ color: C.slate }}>
          read back out of {rec.collection}: {n(rec.count_in_platform)} of {n(rec.count_expected)} record(s)
        </span>
      </div>
      {!!rec.totals?.length && (
        <table className="w-full text-[11px]">
          <thead><tr className="text-left text-[9px] uppercase" style={{ color: C.slate }}>
            <th className="py-1 pr-2">Control total</th><th className="py-1 pr-2">In the extract</th>
            <th className="py-1 pr-2">In the platform</th><th className="py-1">Difference</th>
          </tr></thead>
          <tbody>
            {rec.totals.map(t => (
              <tr key={t.field} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                <td className="py-1 pr-2" style={{ color: C.navy }}>{t.label}</td>
                <td className="py-1 pr-2 font-mono" style={{ color: C.slate }}>{money(t.expected)}</td>
                <td className="py-1 pr-2 font-mono" style={{ color: C.navy }}>{money(t.in_platform)}</td>
                <td className="py-1 font-mono font-semibold" style={{ color: Math.abs(t.difference) < 0.01 ? C.green : C.red }}>
                  {money(t.difference)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LoadResult({ result }) {
  const refused = result.status === 'refused';
  const download = () => {
    const blob = new Blob([result.rejects_csv || ''], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `load-${result.load_id}-rejects.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Card className="rounded-md bg-white p-4 space-y-3" data-testid="loader-result"
      style={{ border: `1px solid ${refused ? 'rgba(226,75,74,0.35)' : 'rgba(136,150,168,0.12)'}` }}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" data-testid="loader-status-badge"
          style={{ background: (refused ? C.red : C.green) + '18', color: refused ? C.red : C.green }}>
          {refused ? 'refused' : 'loaded'}
        </span>
        <span className="text-xs font-semibold" style={{ color: C.navy }}>{result.label} · {result.filename}</span>
        <span className="text-[10px]" style={{ color: C.slate }}>
          {result.mode} mode · map v{result.set_version} · load {result.load_id}
        </span>
        {!!result.rejects_csv && (
          <Button size="sm" variant="ghost" onClick={download} className="ml-auto text-[11px] h-6 px-2 rounded-sm"
            style={{ color: C.blue }} data-testid="loader-download-rejects-btn">
            <Download className="w-3 h-3 mr-1" />Reject file
          </Button>
        )}
      </div>
      <p className="text-[11px]" style={{ color: refused ? C.red : C.slate }} data-testid="loader-note">{result.note}</p>
      <Counts counts={result.counts || {}} />
      <Reconciliation rec={result.reconciliation} />
      {!!result.reject_summary?.length && (
        <div className="space-y-1" data-testid="loader-reject-summary">
          <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>Why rows were rejected</p>
          {result.reject_summary.slice(0, 8).map(f => (
            <div key={f.canonical_field} className="flex items-start gap-2 text-[11px] p-1.5 rounded-sm" style={{ background: '#FDF7EC' }}>
              <span className="font-mono font-bold w-10 text-right" style={{ color: C.red }}>{n(f.count)}</span>
              <span style={{ color: C.navy }}>{f.label}</span>
              <span className="text-[10px]" style={{ color: C.slate }}>{f.examples?.[0]}</span>
            </div>
          ))}
        </div>
      )}
      {!!result.conflicts?.length && (
        <div className="space-y-1" data-testid="loader-conflicts">
          <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>
            Skipped — already in the platform (nothing was overwritten)
          </p>
          {result.conflicts.slice(0, 6).map((c, i) => (
            <p key={`${c.row}-${i}`} className="text-[11px]" style={{ color: C.slate }}>row {c.row}: {c.reason}</p>
          ))}
        </div>
      )}
    </Card>
  );
}

function LoadForm({ api, sets, onLoaded }) {
  const [setId, setSetId] = useState('');
  const [mode, setMode] = useState('strict');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!setId || !file) { toast.error('Pick an approved map and an extract'); return; }
    setBusy(true);
    const form = new FormData();
    form.append('set_id', setId);
    form.append('mode', mode);
    form.append('file', file);
    try {
      const { data } = await api.post('/migration/loads', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      data.status === 'refused'
        ? toast.error(`Refused — ${n(data.counts.rejected_rows)} row(s) rejected, nothing written`)
        : toast.success(`${n(data.counts.inserted)} record(s) loaded${data.reconciliation?.balanced ? ' and reconciled' : ' — does not balance'}`);
      onLoaded(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'The load failed');
    } finally { setBusy(false); }
  };

  return (
    <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="loader-card">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate }}>Load a book</h3>
      <p className="text-[11px] mb-3" style={{ color: C.slate }}>
        Only an approved map loads, through the same transformation the dry run proved. Rows are inserted, never
        overwritten — anything already in the platform is skipped and named, and the whole load can be rolled back.
      </p>
      <div className="space-y-2">
        <select value={setId} onChange={e => setSetId(e.target.value)} data-testid="loader-set-select"
          className="w-full text-xs rounded-sm h-8 px-2 border" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
          <option value="">Approved mapping set…</option>
          {sets.map(s => (
            <option key={s.set_id} value={s.set_id}>
              {s.label} v{s.version} · {s.client || 'no client'} · {s.source_system || 'source'}
            </option>
          ))}
        </select>
        {!sets.length && (
          <p className="text-[11px]" style={{ color: C.amber }} data-testid="loader-no-approved-sets">
            No approved mapping set yet. Agree a map in the workbench first — a load is the map, executed.
          </p>
        )}
        <input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)}
          className="text-xs" data-testid="loader-file-input" />
        <div className="flex flex-wrap gap-3 items-center">
          {[['strict', 'Strict — one rejected row and nothing lands'], ['partial', 'Partial — load the clean rows, list the rest']].map(([key, label]) => (
            <label key={key} className="flex items-center gap-1.5 text-[11px] cursor-pointer" style={{ color: C.navy }}>
              <input type="radio" name="load-mode" checked={mode === key} onChange={() => setMode(key)}
                data-testid={`loader-mode-${key}`} />
              {label}
            </label>
          ))}
        </div>
        <Button size="sm" onClick={run} disabled={busy || !sets.length} className="h-7 text-[11px] rounded-sm"
          style={{ background: C.blue }} data-testid="loader-run-btn">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
          {busy ? 'Loading…' : 'Load the extract'}
        </Button>
      </div>
    </Card>
  );
}

function LoadHistory({ api, loads, ready, isAdmin, onChanged }) {
  const [openId, setOpenId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const rollback = async (load_id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/migration/loads/${load_id}/rollback`, { reason });
      toast.success(`${n(data.deleted)} record(s) removed — ${data.clean ? 'nothing left behind' : `${n(data.remaining)} still present`}`);
      setOpenId(''); setReason('');
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Rollback failed');
    } finally { setBusy(false); }
  };

  return (
    <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="loads-card">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate }}>Loads</h3>
      {!ready ? (
        <p className="text-[11px]" style={{ color: C.slate }} data-testid="loads-loading">Reading the load register…</p>
      ) : !loads.length ? (
        <p className="text-[11px]" style={{ color: C.slate }} data-testid="loads-empty">
          No book has been loaded yet. Every load that ever runs stays on this register, including the refused ones.
        </p>
      ) : (
        <table className="w-full text-xs" data-testid="loads-table">
          <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
            <th className="py-1 pr-2">When</th><th className="py-1 pr-2">Shape</th><th className="py-1 pr-2">Client</th>
            <th className="py-1 pr-2">Mode</th><th className="py-1 pr-2">Rows</th><th className="py-1 pr-2">Inserted</th>
            <th className="py-1 pr-2">Reconciled</th><th className="py-1 pr-2">Status</th><th className="py-1">By</th>
          </tr></thead>
          <tbody>
            {loads.map(l => (
              <React.Fragment key={l.load_id}>
                <tr className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`load-row-${l.load_id}`}>
                  <td className="py-1.5 pr-2" style={{ color: C.navy }}>{l.loaded_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>{l.label}</td>
                  <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>{l.client || '—'}</td>
                  <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>{l.mode}</td>
                  <td className="py-1.5 pr-2 font-mono" style={{ color: C.navy }}>{n(l.counts?.rows)}</td>
                  <td className="py-1.5 pr-2 font-mono" style={{ color: l.counts?.inserted ? C.green : C.slate }}>{n(l.counts?.inserted)}</td>
                  <td className="py-1.5 pr-2 text-[10px] font-bold uppercase"
                    style={{ color: l.reconciliation?.balanced ? C.green : C.red }}>
                    {l.counts?.inserted ? (l.reconciliation?.balanced ? 'balanced' : 'off') : '—'}
                  </td>
                  <td className="py-1.5 pr-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                      style={{
                        background: (l.status === 'loaded' ? C.green : l.status === 'rolled_back' ? C.slate : C.red) + '18',
                        color: l.status === 'loaded' ? C.green : l.status === 'rolled_back' ? C.slate : C.red,
                      }}>{(l.status || '').replace('_', ' ')}</span>
                  </td>
                  <td className="py-1.5 text-[10px]" style={{ color: C.slate }}>
                    {l.loaded_by || '—'}
                    {isAdmin && l.status === 'loaded' && (
                      <Button size="sm" variant="ghost" onClick={() => { setOpenId(openId === l.load_id ? '' : l.load_id); setReason(''); }}
                        className="ml-2 h-5 px-1.5 text-[10px] rounded-sm" style={{ color: C.red }}
                        data-testid={`load-rollback-btn-${l.load_id}`}>
                        <Undo2 className="w-3 h-3 mr-1" />Roll back
                      </Button>
                    )}
                  </td>
                </tr>
                {openId === l.load_id && (
                  <tr data-testid={`load-rollback-row-${l.load_id}`}>
                    <td colSpan={9} className="p-2" style={{ background: 'rgba(226,75,74,0.05)' }}>
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-[11px]" style={{ color: C.red }}>
                          Deletes the {n(l.counts?.inserted)} record(s) this load inserted into {l.collection}, and nothing else.
                        </span>
                        <Input value={reason} onChange={e => setReason(e.target.value)} data-testid="load-rollback-reason"
                          placeholder="Why is this load being undone? (recorded, min 10 chars)"
                          className="flex-1 min-w-[240px] h-7 text-[11px] rounded-sm" />
                        <Button size="sm" onClick={() => rollback(l.load_id)} disabled={busy || reason.trim().length < 10}
                          className="h-7 text-[11px] rounded-sm" style={{ background: C.red }}
                          data-testid={`load-rollback-confirm-${l.load_id}`}>
                          {busy ? 'Rolling back…' : 'Roll back the load'}
                        </Button>
                      </div>
                      {!!l.rollback_reason && <p className="text-[10px] mt-1" style={{ color: C.slate }}>{l.rollback_reason}</p>}
                    </td>
                  </tr>
                )}
                {l.status === 'rolled_back' && (
                  <tr><td colSpan={9} className="pb-1.5 text-[10px]" style={{ color: C.slate }} data-testid={`load-rolledback-note-${l.load_id}`}>
                    rolled back {l.rolled_back_at?.slice(0, 16).replace('T', ' ')} by {l.rolled_back_by} — {n(l.rolled_back_count)} removed · {l.rollback_reason}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export function MigrationLoader({ api, isAdmin }) {
  const [sets, setSets] = useState([]);
  const [loads, setLoads] = useState([]);
  const [ready, setReady] = useState(false);
  const [result, setResult] = useState(null);

  const refresh = useCallback(() => {
    api.get('/migration/loads').then(r => setLoads(r.data.loads || []))
      .catch(() => { }).finally(() => setReady(true));
  }, [api]);

  useEffect(() => {
    api.get('/migration/mappings').then(r => setSets((r.data.sets || []).filter(
      s => s.status === 'approved' && !s.superseded_by))).catch(() => { });
    refresh();
  }, [api, refresh]);

  return (
    <div className="space-y-3" data-testid="migration-loader">
      {isAdmin ? (
        <LoadForm api={api} sets={sets} onLoaded={(r) => { setResult(r); refresh(); }} />
      ) : (
        <p className="text-[11px] p-2 rounded-sm" style={{ background: 'rgba(136,150,168,0.08)', color: C.slate }}
          data-testid="loader-read-only-note">
          Loading a client's book is an admin decision. Every load and rollback is readable here.
        </p>
      )}
      {result && <LoadResult result={result} />}
      <LoadHistory api={api} loads={loads} ready={ready} isAdmin={isAdmin} onChanged={refresh} />
    </div>
  );
}
